import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Avatar, Empty, ErrorView, Field, Loading, Page, Sheet, useAddParam, useFail, useToast } from "../components/ui.tsx";
import { api, ApiError } from "../lib/api.ts";
import { label, phone, when } from "../lib/format.ts";
import { useMe } from "../lib/session.tsx";

const ROLES = ["buyer", "seller_prospect", "vendor", "past_client", "investor", "solicitor", "broker", "referrer"];
const FILTER_KEYS = ["role", "tag", "suburb", "region", "assigned", "price_min", "price_max", "needs_review", "open_deal"];

export function People() {
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const [text, setText] = useState(sp.get("q") ?? "");
  const [sheet, setSheet] = useState<null | "filters" | "add" | "save">(null);
  useAddParam(() => setSheet("add"));
  useEffect(() => {
    const t = setTimeout(() => { const n = new URLSearchParams(sp); text ? n.set("q", text) : n.delete("q"); setSp(n, { replace: true }); }, 250);
    return () => clearTimeout(t);
  }, [text]);
  const params = new URLSearchParams([...sp].filter(([k]) => k === "q" || FILTER_KEYS.includes(k)));
  params.set("limit", "100");
  const q = useQuery({ queryKey: ["people", params.toString()], queryFn: () => api.get(`/people?${params}`) });
  const views = useQuery({ queryKey: ["saved-views", "people"], queryFn: () => api.get<any[]>("/saved-views?screen=people") });
  const active = FILTER_KEYS.filter((k) => sp.get(k));
  const apply = (query: Record<string, unknown>) => {
    const n = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v != null && v !== "") n.set(k, String(v));
    setText(""); setSp(n, { replace: true });
  };
  const viewOn = (v: any) => Object.entries(v.query).every(([k, val]) => sp.get(k) === String(val)) && active.length === Object.keys(v.query).length;

  return (
    <Page title="People" actions={<button className="iconbtn" aria-label="Add a person" onClick={() => setSheet("add")}><Icon name="plus" /></button>}>
      <div className="search"><Icon name="search" /><input value={text} onChange={(e) => setText(e.target.value)} placeholder="Name, number, email or suburb" type="search" aria-label="Search people" /></div>
      <div className="chips">
        <button className={"chip" + (active.length ? " on" : "")} onClick={() => setSheet("filters")}><Icon name="filter" size={15} />Filters{active.length ? ` · ${active.length}` : ""}</button>
        {(views.data ?? []).map((v) => (
          <button key={v.id} className={"chip" + (viewOn(v) ? " on" : "")} onClick={() => (viewOn(v) ? apply({}) : apply(v.query))}>{v.name}</button>
        ))}
        {["buyer", "seller_prospect", "vendor"].map((r) => (
          <button key={r} className={"chip" + (sp.get("role") === r && active.length === 1 ? " on" : "")} onClick={() => apply(sp.get("role") === r ? {} : { role: r })}>{label(r)}s</button>
        ))}
        {active.length > 0 && <button className="chip" onClick={() => setSheet("save")}>Save as call list</button>}
      </div>
      {q.isLoading ? <Loading /> : q.error ? <ErrorView error={q.error} /> : (
        <>
          <div className="section-title" style={{ marginTop: 4 }}><span className="sq" />{q.data.total} {q.data.total === 1 ? "person" : "people"}</div>
          {q.data.rows.length === 0 ? <Empty>No one matches.</Empty> : (
            <div className="list">
              {q.data.rows.map((p: any) => (
                <Link key={p.id} to={`/people/${p.id}`} className="row">
                  <Avatar name={p.display_name} />
                  <div className="main-col">
                    <div className="title">{p.display_name}{p.needs_review && <span className="badge" style={{ marginLeft: 8 }}>Check</span>}{p.do_not_contact && <span className="badge danger" style={{ marginLeft: 8 }}>No contact</span>}</div>
                    <div className="sub">{[p.roles.map(label).join(", "), p.mobile ? phone(p.mobile) : p.email, p.suburbs].filter(Boolean).join(" · ")}</div>
                  </div>
                  <span className="when">{when(p.last_activity_at)}</span>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
      {sheet === "filters" && <FilterSheet initial={Object.fromEntries(FILTER_KEYS.map((k) => [k, sp.get(k) ?? ""]))} onClose={() => setSheet(null)} onApply={(f) => { apply(f); setSheet(null); }} />}
      {sheet === "save" && <SaveViewSheet query={Object.fromEntries(active.map((k) => [k, isNaN(Number(sp.get(k))) || k === "suburb" ? sp.get(k) : Number(sp.get(k))]))}
        onClose={() => setSheet(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ["saved-views"] }); toast("Saved as a call list"); setSheet(null); }} fail={fail} />}
      {sheet === "add" && <AddPersonSheet onClose={() => setSheet(null)} />}
    </Page>
  );
}

function FilterSheet({ initial, onApply, onClose }: { initial: Record<string, string>; onApply: (f: Record<string, unknown>) => void; onClose: () => void }) {
  const [f, setF] = useState(initial);
  const set = (k: string, v: string) => setF({ ...f, [k]: v });
  const dollars = (k: string) => (f[k] ? String(Number(f[k]) / 100) : "");
  return (
    <Sheet title="Filter people" onClose={onClose} foot={<><button className="btn ghost" onClick={() => onApply({})}>Clear</button><button className="btn" onClick={() => onApply(f)}>Show people</button></>}>
      <Field label="Role"><select className="select" value={f.role} onChange={(e) => set("role", e.target.value)}><option value="">Any</option>{ROLES.map((r) => <option key={r} value={r}>{label(r)}</option>)}</select></Field>
      <div className="grid2">
        <Field label="Budget from ($)"><input className="input" inputMode="numeric" value={dollars("price_min")} onChange={(e) => set("price_min", e.target.value ? String(Number(e.target.value.replace(/\D/g, "")) * 100) : "")} placeholder="600000" /></Field>
        <Field label="Budget to ($)"><input className="input" inputMode="numeric" value={dollars("price_max")} onChange={(e) => set("price_max", e.target.value ? String(Number(e.target.value.replace(/\D/g, "")) * 100) : "")} placeholder="900000" /></Field>
      </div>
      <div className="grid2">
        <Field label="Region they want"><input className="input" value={f.region} onChange={(e) => set("region", e.target.value)} placeholder="Ipswich, Granite Belt" /></Field>
        <Field label="Suburb"><input className="input" value={f.suburb} onChange={(e) => set("suburb", e.target.value)} placeholder="Redbank Plains" /></Field>
      </div>
      <div className="grid2">
        <Field label="Tag"><input className="input" value={f.tag} onChange={(e) => set("tag", e.target.value)} /></Field>
        <Field label="Whose"><select className="select" value={f.assigned} onChange={(e) => set("assigned", e.target.value)}><option value="">Anyone's</option><option value="me">Mine</option><option value="none">Nobody's</option></select></Field>
      </div>
      <div className="grid2">
        <Field label="Open deal"><select className="select" value={f.open_deal} onChange={(e) => set("open_deal", e.target.value)}><option value="">Any</option><option value="buyer">Buyer deal</option><option value="seller">Seller deal</option></select></Field>
        <Field label="Needs checking"><select className="select" value={f.needs_review} onChange={(e) => set("needs_review", e.target.value)}><option value="">Any</option><option value="1">Only those</option></select></Field>
      </div>
    </Sheet>
  );
}

function SaveViewSheet({ query, onClose, onSaved, fail }: { query: Record<string, unknown>; onClose: () => void; onSaved: () => void; fail: (e: unknown) => void }) {
  const me = useMe();
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const save = async () => { try { await api.post("/saved-views", { screen: "people", name, query, shared }); onSaved(); } catch (e) { fail(e); } };
  return (
    <Sheet title="Save as a call list" onClose={onClose} foot={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn" disabled={!name} onClick={save}>Save</button></>}>
      <Field label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Buyers $600k–$900k, Ipswich" /></Field>
      {me.user.role !== "agent" && <label className="row" style={{ padding: "8px 0" }}><input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /><span>Share with the team (each person still only sees who they're allowed to)</span></label>}
    </Sheet>
  );
}

function AddPersonSheet({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const fail = useFail();
  const [f, setF] = useState({ first_name: "", last_name: "", mobile: "", email: "", role: "buyer", source: "" });
  const [dup, setDup] = useState<{ id: string; msg: string } | null>(null);
  const set = (k: string, v: string) => setF({ ...f, [k]: v });
  const save = async (force = false) => {
    try {
      const p: any = await api.post("/people", { first_name: f.first_name || undefined, last_name: f.last_name || undefined, mobile: f.mobile || undefined,
        email: f.email || undefined, roles: f.role ? [f.role] : [], source: f.source || undefined, force });
      qc.invalidateQueries({ queryKey: ["people"] });
      nav(`/people/${p.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.code === "exists") setDup({ id: e.details.person_id, msg: e.message }); else fail(e);
    }
  };
  return (
    <Sheet title="Add a person" onClose={onClose} foot={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn" onClick={() => save()} disabled={!f.first_name && !f.mobile && !f.email}>Add</button></>}>
      {dup && <div className="banner" style={{ margin: "0 0 14px" }}><span>{dup.msg}. <Link to={`/people/${dup.id}`} className="blue" onClick={onClose}>Open them</Link> or <button className="blue" style={{ background: "none", border: 0, padding: 0 }} onClick={() => save(true)}>add anyway</button>.</span></div>}
      <div className="grid2">
        <Field label="First name"><input className="input" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} autoComplete="off" /></Field>
        <Field label="Last name"><input className="input" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} autoComplete="off" /></Field>
      </div>
      <Field label="Mobile"><input className="input" inputMode="tel" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="04xx xxx xxx" /></Field>
      <Field label="Email"><input className="input" inputMode="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
      <div className="grid2">
        <Field label="They are a"><select className="select" value={f.role} onChange={(e) => set("role", e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{label(r)}</option>)}</select></Field>
        <Field label="Where they came from"><input className="input" value={f.source} onChange={(e) => set("source", e.target.value)} placeholder="Open home" /></Field>
      </div>
    </Sheet>
  );
}

